import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Button } from '@openx/components-core/lib/Button/Button.tsx';
import { DialogTitle } from '@openx/components-core/lib/Dialog/DialogTitle/DialogTitle.tsx';
import { MoreFiltersDrawer } from '@openx/components-filters/lib/FiltersBar/components/moreFilters/MoreFiltersDrawer.tsx';
import type { FiltersConfig } from '@openx/types/chipFilters.ts';
import type { DealsListFiltersValue } from '@openx/types/deal.ts';
import type { BuyerOption } from '@openx/types/options.ts';
import type { AllTableCriteria } from '@openx/types/tableCriteria.ts';
import { appendUniqueMultiValueFilter } from '@openx/utils/lib/multiValueFilter.ts';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDealContext } from '../utils/context/DealContext.tsx';
import { DealsFastFiltersBar } from './DealsFastFiltersBar.tsx';
import { useDealsMoreFiltersDrawer } from './hooks/useDealsMoreFiltersDrawer.ts';
import { useDealsMoreFiltersRememberedValues } from './hooks/useDealsMoreFiltersRememberedValues.ts';
import { DealsMoreFiltersDealTypeField } from './parts/DealsMoreFiltersDealTypeField.tsx';
import { DealsMoreFiltersMetricRangeField } from './parts/DealsMoreFiltersMetricRangeField.tsx';
import { DealsMoreFiltersRememberedSelectField } from './parts/DealsMoreFiltersRememberedSelectField.tsx';
import { DealsMoreFiltersSelectField } from './parts/DealsMoreFiltersSelectField.tsx';
import {
  areBuyerFilterValuesEqual,
  buildBuyerFilterOptions,
  collapseBuyerFilterDraftValue,
  collapseBuyerRememberedValues,
  expandBuyerFilterToSeatIds,
  getOptionalFilterValue,
} from './parts/dealsMoreFiltersShared.ts';

export interface DealsFastFiltersTopBarProps {
  onChange: (filters: DealsListFiltersValue) => void;
  criteria: AllTableCriteria;
  filtersConfig: FiltersConfig;
  dealIdFilterKey?: 'exchange_deal_id' | 'deal_id';
}

type MetricDraftFilterKey =
  | 'spend_min'
  | 'spend_max'
  | 'impressions_min'
  | 'impressions_max'
  | 'cpm_min'
  | 'cpm_max'
  | 'publisher_revenue_min'
  | 'publisher_revenue_max'
  | 'publisher_cpm_min'
  | 'publisher_cpm_max'
  | 'partner_revenue_min'
  | 'partner_revenue_max';

type MetricConfigFilterKey =
  | 'spend'
  | 'impressions'
  | 'cpm'
  | 'publisher_revenue'
  | 'publisher_cpm'
  | 'partner_revenue';

type DrawerDraftFilters = Partial<
  Pick<
    DealsListFiltersValue,
    'deal_id' | 'exchange_deal_id' | 'package_uid' | 'pmp_deal_type' | 'demand_partner' | 'buyer_ids'
  >
> & { [K in MetricDraftFilterKey]?: string };
type DrawerDraftFilterKey =
  | 'package_uid'
  | 'deal_id'
  | 'exchange_deal_id'
  | 'pmp_deal_type'
  | 'demand_partner'
  | 'buyer_ids'
  | MetricConfigFilterKey;
type DealIdFilterKey = NonNullable<DealsFastFiltersTopBarProps['dealIdFilterKey']>;
const DRAWER_OPTIONAL_FILTER_KEYS = [
  'package_uid',
  'pmp_deal_type',
  'demand_partner',
  'buyer_ids',
] as const satisfies readonly DrawerDraftFilterKey[];

const isMetricFilterKey = (key: string): key is MetricConfigFilterKey =>
  ['spend', 'impressions', 'cpm', 'publisher_revenue', 'publisher_cpm', 'partner_revenue'].includes(key);

const getMetricMinMax = (key: MetricConfigFilterKey): [MetricDraftFilterKey, MetricDraftFilterKey] => [
  `${key}_min` as MetricDraftFilterKey,
  `${key}_max` as MetricDraftFilterKey,
];

const getDrawerDraftFilters = (
  filters: DealsListFiltersValue,
  dealIdFilterKey: DealsFastFiltersTopBarProps['dealIdFilterKey'] = 'deal_id',
  buyersMap: Record<string, BuyerOption | undefined> = {}
) =>
  ({
    [dealIdFilterKey]: filters[dealIdFilterKey] ?? '',
    package_uid: filters.package_uid ?? '',
    pmp_deal_type: filters.pmp_deal_type ?? '',
    demand_partner: filters.demand_partner ?? '',
    buyer_ids: collapseBuyerFilterDraftValue(filters.buyer_ids, buyersMap),
    spend_min: filters.spend_min?.toString() ?? '',
    spend_max: filters.spend_max?.toString() ?? '',
    impressions_min: filters.impressions_min?.toString() ?? '',
    impressions_max: filters.impressions_max?.toString() ?? '',
    cpm_min: filters.cpm_min?.toString() ?? '',
    cpm_max: filters.cpm_max?.toString() ?? '',
    publisher_revenue_min: filters.publisher_revenue_min?.toString() ?? '',
    publisher_revenue_max: filters.publisher_revenue_max?.toString() ?? '',
    publisher_cpm_min: filters.publisher_cpm_min?.toString() ?? '',
    publisher_cpm_max: filters.publisher_cpm_max?.toString() ?? '',
    partner_revenue_min: filters.partner_revenue_min?.toString() ?? '',
    partner_revenue_max: filters.partner_revenue_max?.toString() ?? '',
  }) satisfies DrawerDraftFilters;

const areDrawerDraftFiltersEqual = (current: DrawerDraftFilters, next: DrawerDraftFilters): boolean => {
  const currentKeys = Object.keys(current) as (keyof DrawerDraftFilters)[];
  const nextKeys = Object.keys(next) as (keyof DrawerDraftFilters)[];

  return currentKeys.length === nextKeys.length && nextKeys.every(key => current[key] === next[key]);
};

const getEmptyDrawerDraftFilters = (dealIdFilterKey: DealIdFilterKey): DrawerDraftFilters =>
  ({
    [dealIdFilterKey]: '',
    package_uid: '',
    pmp_deal_type: '',
    demand_partner: '',
    buyer_ids: '',
    spend_min: '',
    spend_max: '',
    impressions_min: '',
    impressions_max: '',
    cpm_min: '',
    cpm_max: '',
    publisher_revenue_min: '',
    publisher_revenue_max: '',
    publisher_cpm_min: '',
    publisher_cpm_max: '',
    partner_revenue_min: '',
    partner_revenue_max: '',
  }) satisfies DrawerDraftFilters;

const parseOptionalNumber = (value: string | undefined): number | undefined => {
  const trimmed = value?.trim();
  if (!trimmed) {
    return undefined;
  }
  const parsed = Number(trimmed);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const getAppliedDrawerFilters = (
  drawerDraftFilters: DrawerDraftFilters,
  dealIdFilterKey: DealIdFilterKey,
  buyersMap: Record<string, BuyerOption | undefined> = {}
): Partial<DealsListFiltersValue> => ({
  [dealIdFilterKey]: getOptionalFilterValue(drawerDraftFilters[dealIdFilterKey]?.toString()),
  package_uid: getOptionalFilterValue(drawerDraftFilters.package_uid?.toString()),
  pmp_deal_type: getOptionalFilterValue(drawerDraftFilters.pmp_deal_type?.toString()),
  demand_partner: getOptionalFilterValue(drawerDraftFilters.demand_partner?.toString()),
  buyer_ids: expandBuyerFilterToSeatIds(getOptionalFilterValue(drawerDraftFilters.buyer_ids?.toString()), buyersMap),
  spend_min: parseOptionalNumber(drawerDraftFilters.spend_min),
  spend_max: parseOptionalNumber(drawerDraftFilters.spend_max),
  impressions_min: parseOptionalNumber(drawerDraftFilters.impressions_min),
  impressions_max: parseOptionalNumber(drawerDraftFilters.impressions_max),
  cpm_min: parseOptionalNumber(drawerDraftFilters.cpm_min),
  cpm_max: parseOptionalNumber(drawerDraftFilters.cpm_max),
  publisher_revenue_min: parseOptionalNumber(drawerDraftFilters.publisher_revenue_min),
  publisher_revenue_max: parseOptionalNumber(drawerDraftFilters.publisher_revenue_max),
  publisher_cpm_min: parseOptionalNumber(drawerDraftFilters.publisher_cpm_min),
  publisher_cpm_max: parseOptionalNumber(drawerDraftFilters.publisher_cpm_max),
  partner_revenue_min: parseOptionalNumber(drawerDraftFilters.partner_revenue_min),
  partner_revenue_max: parseOptionalNumber(drawerDraftFilters.partner_revenue_max),
});

const getDrawerDraftFiltersWithPendingDealIdInput = ({
  drawerDraftFilters,
  dealIdFilterKey,
  pendingDealIdInputValue,
  appendDealIds,
}: {
  drawerDraftFilters: DrawerDraftFilters;
  dealIdFilterKey: DealIdFilterKey;
  pendingDealIdInputValue: string;
  appendDealIds: boolean;
}): DrawerDraftFilters => {
  const normalizedDealIdInputValue = pendingDealIdInputValue.trim();

  if (!normalizedDealIdInputValue) {
    return drawerDraftFilters;
  }

  return {
    ...drawerDraftFilters,
    [dealIdFilterKey]: appendDealIds
      ? appendUniqueMultiValueFilter(String(drawerDraftFilters[dealIdFilterKey] ?? ''), normalizedDealIdInputValue)
      : normalizedDealIdInputValue,
  };
};

const getActiveFilterCount = (filters: DealsListFiltersValue, keys: DrawerDraftFilterKey[]): number =>
  keys.reduce((count, key) => {
    if (isMetricFilterKey(key)) {
      const [minKey, maxKey] = getMetricMinMax(key);
      return (filters[minKey] !== null && filters[minKey] !== undefined) ||
        (filters[maxKey] !== null && filters[maxKey] !== undefined)
        ? count + 1
        : count;
    }
    return filters[key as keyof DealsListFiltersValue] ? count + 1 : count;
  }, 0);

const hasMetricFilterValue = (drawerDraftFilters: DrawerDraftFilters, key: MetricConfigFilterKey): boolean => {
  const [minKey, maxKey] = getMetricMinMax(key);
  return (
    Boolean(getOptionalFilterValue(drawerDraftFilters[minKey]?.toString())) ||
    Boolean(getOptionalFilterValue(drawerDraftFilters[maxKey]?.toString()))
  );
};

const hasAppliedMetricFilterValue = (filters: DealsListFiltersValue, key: MetricConfigFilterKey): boolean => {
  const [minKey, maxKey] = getMetricMinMax(key);
  return (
    (filters[minKey] !== null && filters[minKey] !== undefined) ||
    (filters[maxKey] !== null && filters[maxKey] !== undefined)
  );
};

const hasMetricPendingChanges = (
  currentFilters: DealsListFiltersValue,
  drawerDraftFilters: DrawerDraftFilters,
  metricKeys: MetricConfigFilterKey[]
): boolean =>
  metricKeys.some(key => {
    const [minKey, maxKey] = getMetricMinMax(key);
    const draftMin = drawerDraftFilters[minKey] ?? '';
    const draftMax = drawerDraftFilters[maxKey] ?? '';
    const appliedMin = currentFilters[minKey]?.toString() ?? '';
    const appliedMax = currentFilters[maxKey]?.toString() ?? '';
    return draftMin !== appliedMin || draftMax !== appliedMax;
  });

const hasPendingDrawerChanges = ({
  currentFilters,
  dealIdFilterKey,
  drawerDraftFilters,
  drawerFilterKeys,
  buyersMap = {},
}: {
  currentFilters: DealsListFiltersValue;
  dealIdFilterKey: DealIdFilterKey;
  drawerDraftFilters: DrawerDraftFilters;
  drawerFilterKeys: DrawerDraftFilterKey[];
  buyersMap?: Record<string, BuyerOption | undefined>;
}) => {
  const appliedDrawerFilters = getDrawerDraftFilters(currentFilters, dealIdFilterKey, buyersMap);

  const hasExistingChanges = drawerFilterKeys.some(filterKey => {
    if (filterKey === 'buyer_ids') {
      return !areBuyerFilterValuesEqual(currentFilters.buyer_ids, drawerDraftFilters.buyer_ids, buyersMap);
    }

    return String(drawerDraftFilters[filterKey] ?? '') !== String(appliedDrawerFilters[filterKey] ?? '');
  });
  if (hasExistingChanges) {
    return true;
  }

  const metricKeys = drawerFilterKeys.filter(isMetricFilterKey);
  return hasMetricPendingChanges(currentFilters, drawerDraftFilters, metricKeys);
};

const hasSelectedDrawerFilters = ({
  drawerDraftFilters,
  drawerFilterKeys,
}: {
  drawerDraftFilters: DrawerDraftFilters;
  drawerFilterKeys: DrawerDraftFilterKey[];
}) =>
  drawerFilterKeys.some(filterKey => {
    if (isMetricFilterKey(filterKey)) {
      return hasMetricFilterValue(drawerDraftFilters, filterKey);
    }
    return Boolean(getOptionalFilterValue(drawerDraftFilters[filterKey]?.toString()));
  });

const hasAppliedDrawerFilters = ({
  currentFilters,
  dealIdFilterKey,
  drawerFilterKeys,
}: {
  currentFilters: DealsListFiltersValue;
  dealIdFilterKey: DealIdFilterKey;
  drawerFilterKeys: DrawerDraftFilterKey[];
}) => {
  const appliedDrawerFilters = getDrawerDraftFilters(currentFilters, dealIdFilterKey);

  return drawerFilterKeys.some(filterKey => {
    if (isMetricFilterKey(filterKey)) {
      return hasAppliedMetricFilterValue(currentFilters, filterKey);
    }
    return Boolean(getOptionalFilterValue(appliedDrawerFilters[filterKey]?.toString()));
  });
};

export const DealsFastFiltersTopBar = memo(
  ({ onChange, criteria, filtersConfig, dealIdFilterKey = 'deal_id' }: DealsFastFiltersTopBarProps) => {
    const { t } = useTranslation();
    const { useBuyersFetch, useOnBehalfOfAccountId } = useDealContext();
    const filters = criteria.filters as DealsListFiltersValue;
    const { isOpen, openMoreFiltersDrawer, closeMoreFiltersDrawer } = useDealsMoreFiltersDrawer();
    const [phraseInput, setPhraseInput] = useState(filters.phrase ?? '');
    const onBehalfOfAccountId = useOnBehalfOfAccountId();
    const { mapById: buyersMap = {}, loading: isBuyersLoading } = useBuyersFetch(
      null,
      onBehalfOfAccountId,
      Boolean(filtersConfig.buyer_ids)
    );
    const [drawerDraftFilters, setDrawerDraftFilters] = useState<DrawerDraftFilters>(() =>
      getDrawerDraftFilters(filters, dealIdFilterKey, buyersMap)
    );
    const [dealIdInputValue, setDealIdInputValue] = useState('');
    const [isDiscardChangesDialogOpen, setIsDiscardChangesDialogOpen] = useState(false);
    const latestFiltersRef = useRef(filters);
    const {
      rememberedValues: dealIdRememberedValues,
      rememberValue: rememberDealIdValue,
      clearRememberedValues: clearRememberedDealIds,
    } = useDealsMoreFiltersRememberedValues(filters[dealIdFilterKey]);
    const {
      rememberedValues: packageRememberedValues,
      rememberValue: rememberPackageValue,
      clearRememberedValues: clearRememberedPackages,
    } = useDealsMoreFiltersRememberedValues(filters.package_uid);
    const {
      rememberedValues: demandPartnerRememberedValues,
      rememberValue: rememberDemandPartnerValue,
      clearRememberedValues: clearRememberedDemandPartners,
    } = useDealsMoreFiltersRememberedValues(filters.demand_partner);
    const {
      rememberedValues: rawBuyerRememberedValues,
      rememberValue: rememberBuyerIdsValue,
      clearRememberedValues: clearRememberedBuyerIds,
    } = useDealsMoreFiltersRememberedValues(filters.buyer_ids);
    const buyerIdsRememberedValues = useMemo(
      () => collapseBuyerRememberedValues(rawBuyerRememberedValues, buyersMap),
      [rawBuyerRememberedValues, buyersMap]
    );

    const onChangeRef = useRef(onChange);

    useEffect(() => {
      latestFiltersRef.current = filters;
    }, [filters]);

    useEffect(() => {
      onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
      setPhraseInput(filters.phrase ?? '');
    }, [filters.phrase]);

    useEffect(() => {
      if (!isOpen) {
        setDrawerDraftFilters(currentDraftFilters => {
          const nextDraftFilters = getDrawerDraftFilters(filters, dealIdFilterKey, buyersMap);

          return areDrawerDraftFiltersEqual(currentDraftFilters, nextDraftFilters)
            ? currentDraftFilters
            : nextDraftFilters;
        });
        setDealIdInputValue('');
      }
    }, [buyersMap, dealIdFilterKey, filters, isOpen]);

    const flushPhraseFilter = useCallback((nextPhrase: string) => {
      if ((latestFiltersRef.current.phrase ?? '') === nextPhrase) {
        return;
      }

      onChangeRef.current({
        ...latestFiltersRef.current,
        phrase: nextPhrase,
      });
    }, []);

    useEffect(() => {
      const timeoutId = window.setTimeout(() => {
        flushPhraseFilter(phraseInput);
      }, 500);

      return () => window.clearTimeout(timeoutId);
    }, [flushPhraseFilter, phraseInput]);

    const handleOpenMoreFilters = () => {
      setDrawerDraftFilters(getDrawerDraftFilters(latestFiltersRef.current, dealIdFilterKey, buyersMap));
      setDealIdInputValue('');
      openMoreFiltersDrawer();
    };

    const closeMoreFilters = () => {
      setDrawerDraftFilters(getDrawerDraftFilters(latestFiltersRef.current, dealIdFilterKey, buyersMap));
      setDealIdInputValue('');
      closeMoreFiltersDrawer();
    };

    const handleRequestCloseMoreFilters = () => {
      if (
        hasPendingDrawerChanges({
          currentFilters: latestFiltersRef.current,
          dealIdFilterKey,
          drawerDraftFilters: effectiveDrawerDraftFilters,
          drawerFilterKeys,
          buyersMap,
        })
      ) {
        setIsDiscardChangesDialogOpen(true);
        return;
      }

      closeMoreFilters();
    };

    const handleApplyMoreFilters = () => {
      const normalizedDealIdInputValue = dealIdInputValue.trim();
      const nextDrawerDraftFilters = getDrawerDraftFiltersWithPendingDealIdInput({
        drawerDraftFilters,
        dealIdFilterKey,
        pendingDealIdInputValue: normalizedDealIdInputValue,
        appendDealIds: Boolean(filtersConfig[dealIdFilterKey]?.multiple),
      });

      if (normalizedDealIdInputValue) {
        rememberDealIdValue(normalizedDealIdInputValue);
      }

      onChange({
        ...latestFiltersRef.current,
        ...getAppliedDrawerFilters(nextDrawerDraftFilters, dealIdFilterKey, buyersMap),
      });
      setIsDiscardChangesDialogOpen(false);
      setDealIdInputValue('');
      closeMoreFiltersDrawer();
    };

    const handleClearMoreFilters = () => {
      const emptyDrawerDraftFilters = getEmptyDrawerDraftFilters(dealIdFilterKey);
      setDrawerDraftFilters(emptyDrawerDraftFilters);
      setDealIdInputValue('');
      clearRememberedDealIds();
      clearRememberedPackages();
      clearRememberedDemandPartners();
      clearRememberedBuyerIds();
      onChange({
        ...latestFiltersRef.current,
        ...getAppliedDrawerFilters(emptyDrawerDraftFilters, dealIdFilterKey, buyersMap),
      });
      setIsDiscardChangesDialogOpen(false);
      closeMoreFiltersDrawer();
    };

    const drawerFilterKeys = [
      dealIdFilterKey,
      ...DRAWER_OPTIONAL_FILTER_KEYS,
      ...Object.keys(filtersConfig).filter(isMetricFilterKey),
    ].filter(filterKey => Boolean(filtersConfig[filterKey])) as DrawerDraftFilterKey[];
    const effectiveDrawerDraftFilters = getDrawerDraftFiltersWithPendingDealIdInput({
      drawerDraftFilters,
      dealIdFilterKey,
      pendingDealIdInputValue: dealIdInputValue,
      appendDealIds: Boolean(filtersConfig[dealIdFilterKey]?.multiple),
    });
    const moreFiltersActiveCount = getActiveFilterCount(filters, drawerFilterKeys);
    const isAnyDrawerFilterSelected = hasSelectedDrawerFilters({
      drawerDraftFilters: effectiveDrawerDraftFilters,
      drawerFilterKeys,
    });
    const isAnyAppliedDrawerFilterSelected = hasAppliedDrawerFilters({
      currentFilters: latestFiltersRef.current,
      dealIdFilterKey,
      drawerFilterKeys,
    });
    const isDrawerApplyDisabled = !hasPendingDrawerChanges({
      currentFilters: latestFiltersRef.current,
      dealIdFilterKey,
      drawerDraftFilters: effectiveDrawerDraftFilters,
      drawerFilterKeys,
      buyersMap,
    });
    const isDrawerClearDisabled = !isAnyDrawerFilterSelected && !isAnyAppliedDrawerFilterSelected;

    const handleCloseDiscardChangesDialog = () => {
      setIsDiscardChangesDialogOpen(false);
    };

    const handleDiscardDrawerChanges = () => {
      setIsDiscardChangesDialogOpen(false);
      closeMoreFilters();
    };

    const updateDrawerDraftFilter = (filterKey: string, value: string) => {
      setDrawerDraftFilters(currentFilters => ({
        ...currentFilters,
        [filterKey]: value,
      }));
    };

    const renderDrawerField = (filterKey: DrawerDraftFilterKey) => {
      const filterConfig = filtersConfig[filterKey];
      const dataTestByKey: Record<string, string> = {
        deal_id: 'deals-more-filters-deal-id-section',
        exchange_deal_id: 'deals-more-filters-deal-id-section',
        package_uid: 'deals-more-filters-package-section',
        pmp_deal_type: 'deals-more-filters-deal-type-section',
        demand_partner: 'deals-more-filters-demand-partner-section',
        buyer_ids: 'deals-more-filters-buyer-ids-section',
      };

      if (!filterConfig) {
        return null;
      }

      if (isMetricFilterKey(filterKey) && filterConfig.numberFilter) {
        const [minKey, maxKey] = getMetricMinMax(filterKey);
        return (
          <DealsMoreFiltersMetricRangeField
            key={filterKey}
            filterConfig={filterConfig}
            minValue={drawerDraftFilters[minKey] ?? ''}
            maxValue={drawerDraftFilters[maxKey] ?? ''}
            onMinChange={value => updateDrawerDraftFilter(minKey, value)}
            onMaxChange={value => updateDrawerDraftFilter(maxKey, value)}
            dataTest={`deals-more-filters-${filterKey}-section`}
          />
        );
      }

      if (filterKey === 'package_uid') {
        return (
          <DealsMoreFiltersRememberedSelectField
            key={filterKey}
            filterConfig={filterConfig}
            draftValue={String(drawerDraftFilters.package_uid ?? '')}
            onDraftChange={value => updateDrawerDraftFilter('package_uid', value)}
            rememberedValues={packageRememberedValues}
            onRememberValue={rememberPackageValue}
            onClearRememberedValues={clearRememberedPackages}
            rememberedOptionTestIdPrefix="remembered-package-option"
            dataTest={dataTestByKey[filterKey]}
          />
        );
      }

      if (filterKey === dealIdFilterKey) {
        return (
          <DealsMoreFiltersRememberedSelectField
            key={filterKey}
            filterConfig={filterConfig}
            draftValue={String(drawerDraftFilters[filterKey] ?? '')}
            onDraftChange={value => updateDrawerDraftFilter(filterKey, value)}
            rememberedValues={dealIdRememberedValues}
            onRememberValue={rememberDealIdValue}
            onClearRememberedValues={clearRememberedDealIds}
            rememberedOptionTestIdPrefix="remembered-deal-id-option"
            dataTest={dataTestByKey[filterKey]}
            inputValue={dealIdInputValue}
            onInputValueChange={setDealIdInputValue}
          />
        );
      }

      if (filterKey === 'demand_partner') {
        return (
          <DealsMoreFiltersRememberedSelectField
            key={filterKey}
            filterConfig={filterConfig}
            draftValue={String(drawerDraftFilters.demand_partner ?? '')}
            onDraftChange={value => updateDrawerDraftFilter('demand_partner', value)}
            rememberedValues={demandPartnerRememberedValues}
            onRememberValue={rememberDemandPartnerValue}
            onClearRememberedValues={clearRememberedDemandPartners}
            rememberedOptionTestIdPrefix="remembered-demand-partner-option"
            dataTest={dataTestByKey[filterKey]}
          />
        );
      }

      if (filterKey === 'buyer_ids') {
        const buyerFilterConfig = {
          ...filterConfig,
          loading: isBuyersLoading,
          options: buildBuyerFilterOptions(buyersMap),
        };

        return (
          <DealsMoreFiltersRememberedSelectField
            key={filterKey}
            filterConfig={buyerFilterConfig}
            draftValue={String(drawerDraftFilters.buyer_ids ?? '')}
            onDraftChange={value => updateDrawerDraftFilter('buyer_ids', value)}
            rememberedValues={buyerIdsRememberedValues}
            onRememberValue={rememberBuyerIdsValue}
            onClearRememberedValues={clearRememberedBuyerIds}
            rememberedOptionTestIdPrefix="remembered-buyer-ids-option"
            dataTest={dataTestByKey[filterKey]}
          />
        );
      }

      if (filterKey === 'pmp_deal_type') {
        return (
          <DealsMoreFiltersDealTypeField
            key={filterKey}
            filterConfig={filterConfig}
            draftValue={String(drawerDraftFilters.pmp_deal_type ?? '')}
            onDraftChange={value => updateDrawerDraftFilter('pmp_deal_type', value)}
            dataTest={dataTestByKey[filterKey]}
          />
        );
      }

      return (
        <DealsMoreFiltersSelectField
          key={filterKey}
          filterConfig={filterConfig}
          draftValue={String(drawerDraftFilters[filterKey] ?? '')}
          onDraftChange={value => updateDrawerDraftFilter(filterKey, value)}
          onApplyCurrentFilter={value => {
            updateDrawerDraftFilter(filterKey, value);
            onChange({
              ...latestFiltersRef.current,
              [filterKey]: value ? String(value) : undefined,
            });
            closeMoreFiltersDrawer();
          }}
          dataTest={dataTestByKey[filterKey]}
        />
      );
    };

    return (
      <>
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 0,
            borderLeft: 0,
            borderRight: 0,
            backgroundColor: theme => theme.palette.background.boxDark,
          }}
        >
          <Box
            data-test="deals-fast-filters-container"
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <TextField
              type="search"
              size="small"
              variant="outlined"
              placeholder={t('Search Name or Deal ID')}
              value={phraseInput}
              onChange={event => setPhraseInput(event.target.value)}
              data-test="search-field"
              sx={{
                width: { xs: '100%', md: 320 },
                flexShrink: { xs: 0, md: 0 },
                '& .MuiInputBase-root': {
                  height: 40,
                },
              }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                },

                htmlInput: { 'data-test': 'search-field-input' },
              }}
            />

            <DealsFastFiltersBar
              filters={filters}
              filtersConfig={filtersConfig}
              onChange={onChange}
              onOpenMoreFilters={handleOpenMoreFilters}
              moreFiltersActiveCount={moreFiltersActiveCount}
            />
          </Box>
        </Paper>

        <MoreFiltersDrawer
          width={360}
          open={isOpen}
          onClose={handleRequestCloseMoreFilters}
          onApply={handleApplyMoreFilters}
          onClear={handleClearMoreFilters}
          isApplyDisabled={isDrawerApplyDisabled}
          isClearDisabled={isDrawerClearDisabled}
        >
          <Stack
            direction="column"
            sx={{
              gap: 3,
              p: 3,
            }}
          >
            {drawerFilterKeys.map(filterKey => renderDrawerField(filterKey))}
          </Stack>
        </MoreFiltersDrawer>

        <Dialog
          open={isDiscardChangesDialogOpen}
          onClose={handleCloseDiscardChangesDialog}
          data-test="deals-more-filters-discard-dialog"
        >
          <DialogTitle onClose={handleCloseDiscardChangesDialog}>{t('Discard changes')}</DialogTitle>
          <DialogContent dividers>
            <Typography>
              {t(
                "You have selected filters, but they haven't been applied yet. If you close this panel now, your changes will be lost."
              )}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 2, pb: 2 }}>
            <Button variant="text" onClick={handleDiscardDrawerChanges}>
              {t('Discard changes')}
            </Button>
            <Button color="primary" onClick={handleApplyMoreFilters}>
              {t('Apply filters')}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
);
